import { createBrowserRouter, isRouteErrorResponse, useRouteError } from 'react-router'
import { AdminRedirectPage } from './routes/AdminRedirectPage'
import { AccountPage } from './routes/AccountPage'
import { AuthPage, InvitePage } from './routes/AuthPage'
import { CallLogPage } from './routes/CallLogPage'
import { ChatShell } from './routes/ChatShell'
import { ChatDetailsPane } from './routes/ChatDetailsPane'
import { ChatThreadPane } from './routes/ChatThreadPane'
import { IncomingCallRoute } from './routes/IncomingCallRoute'
import { NotFoundPage } from './routes/NotFoundPage'
import { RootLayout } from './routes/RootLayout'

/** The list pane owns the desktop index route; this only fills the second pane slot. */
function NoPane() {
  return null
}

function RootErrorBoundary() {
  const error = useRouteError()
  if (isRouteErrorResponse(error)) {
    return (
      <div role="alert" className="app-shell items-center justify-center gap-2 px-6 text-center">
        <h1 className="text-title font-semibold text-ink">
          {error.status} {error.statusText}
        </h1>
        <p className="text-ui text-muted">{typeof error.data === 'string' ? error.data : 'Request failed.'}</p>
      </div>
    )
  }
  return (
    <div role="alert" className="app-shell items-center justify-center gap-2 px-6 text-center">
      <h1 className="text-title font-semibold text-ink">Something went wrong</h1>
      <p className="text-ui text-muted">{error instanceof Error ? error.message : 'Unknown error'}</p>
    </div>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    Component: RootLayout,
    ErrorBoundary: RootErrorBoundary,
    children: [
      { path: 'login', Component: AuthPage },
      { path: 'i/:code', Component: InvitePage },
      { path: 'call/:callId', Component: IncomingCallRoute },
      { path: 'account', Component: AccountPage },
      // The call log (W6) is its own page, the way the account screen is: reached from the list
      // header, full-page on a phone, and back to the chats from its own header.
      { path: 'calls', Component: CallLogPage },
      {
        Component: ChatShell,
        children: [
          { index: true, Component: NoPane },
          { path: 'chat/:chatId', Component: ChatThreadPane },
          { path: 'chat/:chatId/details', Component: ChatDetailsPane },
        ],
      },
      { path: '*', Component: NotFoundPage },
    ],
  },
  {
    // The admin page used to be a route *inside* the shell. It moved to its own site
    // (docs/CONTRACT.md §11), so this top-level route only forwards an old bookmark to the
    // console: no session needed, no app shell, no LiveKit chunk on the way out.
    path: '/admin',
    Component: AdminRedirectPage,
  },
])
